import React, { useState } from 'react';
import { VaultItem, UserProfile } from '../types';
import {
  FolderLock,
  Plus,
  Search,
  Bookmark,
  FileText,
  Image,
  Mic,
  Trash2,
  Copy,
  Tag,
  Lock,
  Sparkles,
  X,
} from 'lucide-react';
import { soundEffects } from '../lib/soundEffects';

interface VaultViewProps {
  currentUser: UserProfile;
  vaultItems: VaultItem[];
  onAddVaultItem: (item: Partial<VaultItem>) => void;
  onDeleteVaultItem: (id: string) => void;
}

export const VaultView: React.FC<VaultViewProps> = ({
  currentUser,
  vaultItems,
  onAddVaultItem,
  onDeleteVaultItem,
}) => {
  const [activeFolder, setActiveFolder] = useState<string>('الكل');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  // New item form
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newFolder, setNewFolder] = useState('ملاحظات هامة');
  const [newTag, setNewTag] = useState('وتين');

  const folders = ['الكل', 'ملاحظات هامة', 'رسائل وتين', 'وسائط سرية', 'روابط ومراجع'];

  const filteredItems = vaultItems.filter((item) => {
    if (activeFolder !== 'الكل' && item.folder !== activeFolder) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return (
        item.title.toLowerCase().includes(q) ||
        item.content.toLowerCase().includes(q) ||
        item.tags.some((t) => t.toLowerCase().includes(q))
      );
    }
    return true;
  });

  const handleCreateItem = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim() || !newContent.trim()) return;

    onAddVaultItem({
      title: newTitle.trim(),
      content: newContent.trim(),
      folder: newFolder,
      type: 'note',
      tags: newTag.split(',').map((t) => t.trim()).filter(Boolean),
    });

    soundEffects.playSent();
    setNewTitle('');
    setNewContent('');
    setShowAddModal(false);
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-[#060911] font-['Cairo']">
      {/* Top Banner */}
      <div className="max-w-4xl mx-auto mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-black text-white">خزنة وتين المشفرة</h2>
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] font-bold">
              <Lock className="w-3 h-3" />
              <span>مشفرة 256-bit</span>
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-0.5">
            مساحتك الخاصة لحفظ الرسائل، الملاحظات السرية، المستندات، والذكريات الهامة
          </p>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-bold text-xs shadow-lg shadow-emerald-500/20 flex items-center justify-center gap-1.5 transition active:scale-95 shrink-0"
        >
          <Plus className="w-4 h-4" />
          <span>إضافة عنصر للخزنة</span>
        </button>
      </div>

      <div className="max-w-4xl mx-auto space-y-4">
        {/* Search & Folder filters */}
        <div className="flex flex-col sm:flex-row gap-2.5 items-center justify-between">
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="بحث في الخزنة..."
              className="w-full pl-3 pr-9 py-2 bg-[#121927] border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
            />
          </div>

          <div className="flex items-center gap-1.5 overflow-x-auto w-full sm:w-auto no-scrollbar">
            {folders.map((f) => (
              <button
                key={f}
                onClick={() => setActiveFolder(f)}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition shrink-0 ${
                  activeFolder === f
                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-bold'
                    : 'bg-[#121927] text-slate-400 hover:text-white'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Vault Items Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
          {filteredItems.length === 0 ? (
            <div className="col-span-full py-16 text-center text-slate-500">
              <FolderLock className="w-12 h-12 text-slate-600 mx-auto mb-2 stroke-[1.5]" />
              <p className="text-xs">لا توجد عناصر محفوظة في هذا المجلد</p>
            </div>
          ) : (
            filteredItems.map((item) => (
              <div
                key={item.id}
                className="p-4 rounded-2xl bg-[#0e1422] border border-slate-800/80 hover:border-slate-700 transition flex flex-col justify-between group shadow-md"
              >
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="p-1.5 rounded-xl bg-emerald-500/10 text-emerald-400">
                        {item.type === 'note' && <FileText className="w-4 h-4" />}
                        {item.type === 'message' && <Bookmark className="w-4 h-4" />}
                        {item.type === 'media' && <Image className="w-4 h-4" />}
                        {item.type === 'voice' && <Mic className="w-4 h-4" />}
                      </span>
                      <h3 className="text-xs font-bold text-white group-hover:text-emerald-300 transition">
                        {item.title}
                      </h3>
                    </div>

                    <span className="text-[10px] text-slate-500 font-mono">
                      {new Date(item.savedAt).toLocaleDateString('ar-SA')}
                    </span>
                  </div>

                  <p className="text-xs text-slate-300 whitespace-pre-wrap line-clamp-4 leading-relaxed mb-3">
                    {item.content}
                  </p>
                </div>

                <div className="pt-2 border-t border-slate-800/60 flex items-center justify-between">
                  <div className="flex items-center gap-1 flex-wrap">
                    {item.tags?.map((t, idx) => (
                      <span
                        key={idx}
                        className="text-[10px] px-2 py-0.5 rounded-md bg-[#162030] text-slate-400 font-medium"
                      >
                        #{t}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(item.content);
                        soundEffects.playTap();
                      }}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10"
                      title="نسخ المحتوى"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => onDeleteVaultItem(item.id)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10"
                      title="حذف من الخزنة"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ADD ITEM MODAL */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="w-full max-w-md bg-[#0e1422] border border-slate-800 rounded-3xl p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <FolderLock className="w-4 h-4 text-emerald-400" />
                <span>إضافة عنصر إلى خزنة وتين</span>
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-1 text-slate-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleCreateItem} className="space-y-3">
              <div>
                <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                  عنوان العنصر
                </label>
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="مثال: كلمات مرور مهمة، فكرة مشروع..."
                  className="w-full px-3 py-2 bg-[#141b27] border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                  required
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                  المجلد
                </label>
                <select
                  value={newFolder}
                  onChange={(e) => setNewFolder(e.target.value)}
                  className="w-full px-3 py-2 bg-[#141b27] border border-slate-800 rounded-xl text-xs text-white focus:outline-none focus:border-emerald-500"
                >
                  <option value="ملاحظات هامة">ملاحظات هامة</option>
                  <option value="رسائل وتين">رسائل وتين</option>
                  <option value="وسائط سرية">وسائط سرية</option>
                  <option value="روابط ومراجع">روابط ومراجع</option>
                </select>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                  المحتوى
                </label>
                <textarea
                  value={newContent}
                  onChange={(e) => setNewContent(e.target.value)}
                  rows={4}
                  placeholder="اكتب المحتوى السري هنا..."
                  className="w-full px-3 py-2 bg-[#141b27] border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 resize-none"
                  required
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                  الوسوم (مفصولة بفواصل)
                </label>
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="سري, عمل, وتين"
                  className="w-full px-3 py-2 bg-[#141b27] border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                />
              </div>

              <button
                type="submit"
                className="w-full py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-bold text-xs shadow transition"
              >
                تأكيد وحفظ في الخزنة
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
